import { useDialogStore } from '@ariakit/react/dialog'
import { useId, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { checkExperienceErrors } from '@/features/experience-vetting/utils'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { useTranslation } from '@/translations/i18n.tsx'
import type { RemapExperienceVetting } from '../types'
import { ExpVettingCriteriaConfirmationModal } from './exp-vetting-criteria-confirmation-modal'

const MIN_PHOTO_ACCEPTED = 4
export const ExperienceVettingCriteriaHero = ({
	experience
}: {
	experience: RemapExperienceVetting
}) => {
	const { t } = useTranslation()
	const waitlistedId = useId()
	const [isWaitlisted, setIsWaitlisted] = useState(false)
	const hideAction = experience.status !== 'under_review'
	const store = useDialogStore()

	const setIsTouched = useExperienceVettingError(
		(state) => state.setIsTouched
	)
	const setErrors = useExperienceVettingError((state) => state.setErrors)

	const isDeclined = useMemo(
		() => experience.expertise.status === 'declined',
		[experience]
	)
	const isBackToDraft = useMemo(
		() =>
			experience.expertise.status === 'back_to_draft' ||
			experience.itineraries.some(
				(item) =>
					item.status === 'declined' ||
					item.status === 'back_to_draft'
			) ||
			experience.gallery.flags.some(
				(flag) => flag.keyName === 'lack_photo_variety'
			) ||
			experience.gallery.images.filter((img) => img.isApproved).length <
				MIN_PHOTO_ACCEPTED,
		[experience]
	)

	const getSubtitleByService = () => {
		if (isDeclined) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submission_rejected'
			)
		}
		if (isBackToDraft) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submission_back_to_draft'
			)
		}
		return t(
			'vetting:criteria.commons.confirmation_modal.submission_accepted'
		)
	}

	const getToastMessage = () => {
		if (isDeclined) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submit_rejected_toast'
			)
		}
		if (isBackToDraft) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submit_back_to_draft_toast'
			)
		}
		return t(
			'vetting:criteria.commons.confirmation_modal.submit_accepted_toast'
		)
	}

	const onClickSubmit = () => {
		setIsTouched(true)
		const errorCheck = checkExperienceErrors(experience)
		setErrors(errorCheck.errors)
		if (errorCheck.isValid) {
			store.toggle()
		}
	}

	return (
		<OpsVettingHero fullWidth justify={'space-between'}>
			<h3>{t('vetting:criteria.commons.criteria')}</h3>
			{!hideAction && (
				<div className='flex items-center gap-4'>
					<Checkbox
						checked={isWaitlisted}
						id={waitlistedId}
						label={t('vetting:criteria.commons.waitlisted')}
						onChange={() => setIsWaitlisted(!isWaitlisted)}
					/>
					<ButtonBrandLegacy onClick={onClickSubmit} size={'sm'}>
						{t('common:submit')}
					</ButtonBrandLegacy>
				</div>
			)}

			<ExpVettingCriteriaConfirmationModal
				description={getSubtitleByService()}
				experienceId={experience.id}
				isWaitlisted={isWaitlisted}
				store={store}
				toastMessage={getToastMessage()}
			/>
		</OpsVettingHero>
	)
}
